import { useEffect, useRef, useState } from "react";
import {
  Linkedin,
  Loader2,
  Upload,
  Users,
  MessageSquare,
  FileText,
  UserPlus,
} from "lucide-react";
import { getCsrfToken } from "../lib/auth";
import { ScopeBadge, type Scope } from "../components/ScopePicker";
import { TeachingBanner } from "../components/TeachingBanner";

/** LinkedIn connector settings.
 *
 *  Mounted at /settings/connectors/linkedin. LinkedIn has no usable
 *  personal-data API, so this is a "Get a copy of your data" archive
 *  import: the member requests the export at
 *  linkedin.com/mypreferences/d/download-my-data, downloads the zip,
 *  and uploads it here. Read-only: never sends a DM, never accepts an
 *  invitation, never reacts. Configure-first, same as the other
 *  social data-export connectors. */

interface LinkedInStatus {
  ok: boolean;
  configured: boolean;
  account: string | null;
  scope: string | null;
  last_run_at: string | null;
  counts: {
    connection_count: number;
    message_count: number;
    post_count: number;
    invitation_count: number;
  };
  runs: Array<{
    run_id: string;
    started_at: string;
    finished_at: string | null;
    items_ingested: number;
    status: string;
  }>;
}

const SCOPE_CHOICES: Scope[] = ["personal", "work", "family", "private"];

export function LinkedIn() {
  const [status, setStatus] = useState<LinkedInStatus | null>(null);
  const [label, setLabel] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [scope, setScope] = useState<Scope>("personal");
  const [busy, setBusy] = useState<"none" | "configure" | "upload">("none");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const refresh = async () => {
    try {
      const r = await fetch("/api/connectors/linkedin/status", {
        credentials: "same-origin",
      });
      if (!r.ok) return;
      setStatus((await r.json()) as LinkedInStatus);
    } catch {
      /* best effort */
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5_000);
    return () => clearInterval(t);
  }, []);

  const post = async (path: string, body: unknown) => {
    const csrf = getCsrfToken();
    return fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        ...(csrf ? { "X-CSRF-Token": csrf } : {}),
      },
      body: JSON.stringify(body),
    });
  };

  const configure = async () => {
    setBusy("configure");
    setErr(null);
    setMsg(null);
    try {
      const r = await post("/api/connectors/linkedin/configure", {
        account_label: label,
        account_identifier: identifier,
        scope,
      });
      const j = await r.json();
      if (!r.ok) {
        setErr(j.message || j.error || `configure -> ${r.status}`);
        return;
      }
      setMsg("LinkedIn connector configured. Upload your data archive below.");
      refresh();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy("none");
    }
  };

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setErr("Choose your LinkedIn export .zip first.");
      return;
    }
    setBusy("upload");
    setErr(null);
    setMsg(null);
    const csrf = getCsrfToken();
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch("/api/connectors/linkedin/import-export", {
        method: "POST",
        credentials: "same-origin",
        headers: { ...(csrf ? { "X-CSRF-Token": csrf } : {}) },
        body: fd,
      });
      const j = await r.json();
      if (!r.ok) {
        setErr(j.message || j.error || `upload -> ${r.status}`);
        return;
      }
      setMsg("Archive uploaded. Parsing now; counts update below shortly.");
      if (fileRef.current) fileRef.current.value = "";
      refresh();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy("none");
    }
  };

  const configured = status?.configured === true;
  const c = status?.counts;

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-5">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <div className="text-xs uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500">
            Connectors / LinkedIn
          </div>
          {status?.scope && <ScopeBadge scope={status.scope} />}
        </div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          LinkedIn
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-2xl">
          Imports your connections, messages, posts, invitations, and profile
          from a LinkedIn data archive. Request it at{" "}
          <span className="font-mono text-xs">
            linkedin.com/mypreferences/d/download-my-data
          </span>
          . Read-only: Memso never sends a message, accepts an invitation, or
          reacts on your behalf.
        </p>
      </header>

      <TeachingBanner id="linkedin_overview" />

      {err && (
        <div className="glass rounded-xl border border-rose-300 dark:border-rose-700 p-3 text-xs text-rose-700 dark:text-rose-300">
          {err}
        </div>
      )}
      {msg && (
        <div className="glass rounded-xl border border-sky-200 dark:border-sky-800 p-3 text-xs text-sky-700 dark:text-sky-300">
          {msg}
        </div>
      )}

      <section className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#0a66c2] text-white flex items-center justify-center">
            <Linkedin className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
              {configured ? "Connected" : "Set up LinkedIn"}
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {configured
                ? `Configured as ${status?.account ?? "your account"}. Upload a fresh export anytime; re-imports only ingest the delta.`
                : "Name this account and pick a scope before the first upload."}
            </p>
          </div>
        </div>

        {!configured && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
                  Account label
                </span>
                <input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="My LinkedIn"
                  className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
                  Account identifier
                </span>
                <input
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="linkedin.com/in/your-handle"
                  className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-sm"
                />
              </label>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {SCOPE_CHOICES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScope(s)}
                  aria-pressed={scope === s}
                  className={
                    "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold border-2 transition-all " +
                    (scope === s
                      ? "border-sky-500 bg-sky-500/10 text-sky-700 dark:text-sky-200"
                      : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-sky-400")
                  }
                >
                  {s}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={configure}
              disabled={busy !== "none"}
              className="inline-flex items-center gap-1.5 rounded-md bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2"
            >
              {busy === "configure" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : null}
              Save and continue
            </button>
          </div>
        )}

        {configured && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept=".zip"
                className="text-xs text-slate-600 dark:text-slate-300"
              />
              <button
                type="button"
                onClick={upload}
                disabled={busy !== "none"}
                className="inline-flex items-center gap-1.5 rounded-md bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2"
              >
                {busy === "upload" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                Upload archive
              </button>
            </div>

            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <Stat
                Icon={Users}
                label="Connections"
                value={String(c?.connection_count ?? 0)}
              />
              <Stat
                Icon={MessageSquare}
                label="Messages"
                value={String(c?.message_count ?? 0)}
              />
              <Stat
                Icon={FileText}
                label="Posts"
                value={String(c?.post_count ?? 0)}
              />
              <Stat
                Icon={UserPlus}
                label="Invitations"
                value={String(c?.invitation_count ?? 0)}
              />
            </dl>
            <p className="text-[11px] text-slate-400 dark:text-slate-500">
              Last run:{" "}
              {status?.last_run_at
                ? new Date(status.last_run_at).toLocaleString()
                : "never"}
            </p>
          </div>
        )}
      </section>

      {configured && (
        <section className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 p-5">
          <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-3">
            Run history
          </h3>
          {status && status.runs.length > 0 ? (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-400 dark:text-slate-500 uppercase tracking-wide">
                  <th className="py-1 pr-3">Run</th>
                  <th className="py-1 pr-3">Started</th>
                  <th className="py-1 pr-3">Finished</th>
                  <th className="py-1 pr-3">Ingested</th>
                  <th className="py-1">Status</th>
                </tr>
              </thead>
              <tbody>
                {status.runs.map((run) => (
                  <tr
                    key={run.run_id}
                    className="border-t border-slate-100 dark:border-slate-800"
                  >
                    <td className="py-1.5 pr-3 font-mono">
                      {run.run_id.slice(0, 16)}…
                    </td>
                    <td className="py-1.5 pr-3">
                      {new Date(run.started_at).toLocaleString()}
                    </td>
                    <td className="py-1.5 pr-3">
                      {run.finished_at
                        ? new Date(run.finished_at).toLocaleString()
                        : "—"}
                    </td>
                    <td className="py-1.5 pr-3">{run.items_ingested}</td>
                    <td className="py-1.5">
                      <span
                        className={
                          run.status === "done"
                            ? "text-emerald-600 dark:text-emerald-400"
                            : run.status === "error"
                              ? "text-rose-600 dark:text-rose-400"
                              : "text-amber-600 dark:text-amber-400"
                        }
                      >
                        {run.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              No imports yet. Upload your LinkedIn archive to get started.
            </p>
          )}
        </section>
      )}
    </div>
  );
}

function Stat({
  Icon,
  label,
  value,
}: {
  Icon: typeof Users;
  label: string;
  value: string;
}) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500 flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {label}
      </dt>
      <dd className="text-sm font-semibold text-slate-800 dark:text-slate-100">
        {value}
      </dd>
    </div>
  );
}
